import { Suspense } from 'react';
import { useRouteContext } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import type { LegalContentType } from '@/content/legal-content';
import { Header } from './header';
import { Footer } from './footer';
import { Layout } from './layout';
import { PageSeo } from './page-seo';

/** Not `unreset-tailwind` (as `www` uses on the same html): its `all: revert` drops the brand face and ink. */
const LEGAL_PROSE = [
	'mx-auto max-w-3xl px-6 py-16 md:py-24',
	'[&_h1]:mb-8 [&_h1]:text-4xl [&_h1]:font-semibold [&_h1]:tracking-tight md:[&_h1]:text-5xl',
	'[&_h2]:mt-12 [&_h2]:mb-4 [&_h2]:text-2xl [&_h2]:font-medium',
	'[&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:text-xl [&_h3]:font-medium',
	'[&_p]:text-ink/80 [&_p]:mb-4 [&_p]:leading-[1.6]',
	'[&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6',
	'[&_li]:text-ink/80 [&_li]:mb-2 [&_li]:leading-[1.6]',
	'[&_a]:text-brand-strong [&_a]:break-words [&_a]:underline [&_a]:underline-offset-2',
	'[&_strong]:text-ink [&_strong]:font-medium',
	'[&_table]:my-6 [&_table]:w-full [&_table]:text-left [&_th]:border-b [&_th]:p-2 [&_td]:border-b [&_td]:p-2',
].join(' ');

/**
 * Its own component so only the article suspends. `dangerouslySetInnerHTML` over markdown that ships in
 * the bundle (`src/content/**.md`), never visitor input.
 */
function LegalArticle({ type }: { type: LegalContentType }) {
	const { locale } = useRouteContext({ from: '__root__' });
	const { data: html } = useSuspenseQuery(trpc.core.getLegalContent.queryOptions({ type, locale }));
	return <article className={LEGAL_PROSE} dangerouslySetInnerHTML={{ __html: html }} />;
}

/**
 * Own routes, not links to `www`'s copies: the router picks the slug context from the host
 * (`slugContextFor`), so such a link 404s on the redesign host (TEC-3821 B7). Content is shared, with
 * an English fallback.
 */
export function LegalPage(props: {
	type: LegalContentType;
	slugs: Record<string, string>;
	title: string;
	description: string;
}) {
	return (
		<Layout>
			<Header />
			<PageSeo slugs={props.slugs} title={props.title} description={props.description} />
			<Suspense fallback={<div className="min-h-[60vh]" />}>
				<LegalArticle type={props.type} />
			</Suspense>
			<Footer />
		</Layout>
	);
}
